/* ============================================================
   SAM-981 — a residential pass (invitation) that has ended takes no change: the server answers 409
   `pass_expired` to cancelling, check-in and edits. The admin hides those actions by date so nobody
   clicks into the refusal. The server still decides; this only mirrors its rule:

     a pass ends at `invitation_to`; without one, at 08:00 the day after `invitation_from`
     (when its door PIN ends, SAM-977), both in Dominican Republic time (UTC-4, no DST).

   Business passes (`business_access`) are out of this rule.
   ============================================================ */

const RD_OFFSET = '-04:00';
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/;

/** Parses an API date; a value without an offset is Dominican Republic wall-clock time. null if unreadable. */
function parseApiDate(value: unknown): { at: Date } | null {
  const m = DATE_RE.exec(String(value ?? '').trim());
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss, zone] = m;
  const offset = zone ? (zone === 'Z' ? 'Z' : zone.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2')) : RD_OFFSET;
  const at = new Date(`${y}-${mo}-${d}T${hh ?? '00'}:${mm ?? '00'}:${ss ?? '00'}${offset}`);
  return Number.isNaN(at.getTime()) ? null : { at };
}

/** The instant a residential pass ends, or null when its dates are unreadable. */
export function passEndsAt(row: { start_date?: string | null; end_date?: string | null }): Date | null {
  const end = parseApiDate(row.end_date);
  if (end) {
    // A bare day is 00:00 of that day, the same as the server (SAM-981).
    return end.at;
  }
  const start = parseApiDate(row.start_date);
  // Without an end: 08:00 the next day (start is a day; take its calendar day, not its clock time).
  if (start) return new Date(new Date(`${localDay(start.at)}T08:00:00${RD_OFFSET}`).getTime() + 24 * 3600_000);
  return null;
}

/** 'YYYY-MM-DD' of an instant in Dominican Republic time. */
function localDay(at: Date): string {
  const rd = new Date(at.getTime() - 4 * 3600_000);
  return rd.toISOString().slice(0, 10);
}

/** True for a residential pass whose end has passed; business passes and unreadable dates are never "expired" here. */
export function isPassExpired(
  row: { entity?: string | null; start_date?: string | null; end_date?: string | null },
  now: Date = new Date(),
): boolean {
  if (row.entity === 'business_access') return false;
  const ends = passEndsAt(row);
  return ends !== null && ends.getTime() <= now.getTime();
}
